import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { ActivityHistoryEntry, ActivityHistoryQuery } from '@evidentid/tprm-portal-lib/models/activity-history';

const LIMIT = 25;

export interface GlobalEventsRequirement {
    gatewayClient: GatewayApiClient;
}

export interface GlobalEventsStatus {
    loading: boolean;
    finished: boolean;
    list: ActivityHistoryEntry[];
    filters: ActivityHistoryQuery;
    error: boolean;
}

interface GlobalEventsState {
    globalEvents: Record<string, GlobalEventsStatus>;
}

function createEmptyStatus(): GlobalEventsStatus {
    return { loading: false, finished: false, list: [], filters: {}, error: false };
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): GlobalEventsRequirement {
    return store.$app;
}

// loadGlobalEvents sets a new filter on the store immediately, even while a load for the same
// rpName is already in flight. Recording the rpName here lets that in-flight load, once it
// settles, re-issue itself with the filters that are current by then, rather than the filter
// change being silently dropped because a request was already running.
const pendingReload = new Set<string>();

export const useGlobalEventsStore = defineStore('globalEvents', {
    state: (): GlobalEventsState => ({
        globalEvents: {},
    }),
    actions: {
        startLoading(payload: { rpName: string, reset?: boolean }) {
            const current = this.globalEvents[payload.rpName] || createEmptyStatus();
            this.globalEvents = {
                ...this.globalEvents,
                [payload.rpName]: {
                    ...current,
                    loading: true,
                    error: false,
                    ...(payload.reset ? { list: [], finished: false } : {}),
                },
            };
        },

        finishLoading(
            payload: { rpName: string, records: ActivityHistoryEntry[], finished: boolean, append: boolean },
        ) {
            const current = this.globalEvents[payload.rpName];
            if (!current) {
                // The entry was cleared (e.g. the user switched away from this RP) while this
                // request was still in flight; there is nothing left to update.
                return;
            }
            this.globalEvents = {
                ...this.globalEvents,
                [payload.rpName]: {
                    ...current,
                    loading: false,
                    finished: payload.finished,
                    list: payload.append ? [ ...current.list, ...payload.records ] : payload.records,
                },
            };
        },

        failLoading(payload: { rpName: string }) {
            const current = this.globalEvents[payload.rpName];
            if (!current) {
                return;
            }
            this.globalEvents = {
                ...this.globalEvents,
                [payload.rpName]: { ...current, loading: false, error: true },
            };
        },

        setFilters(payload: { rpName: string, filters: ActivityHistoryQuery }) {
            const current = this.globalEvents[payload.rpName] || createEmptyStatus();
            this.globalEvents = { ...this.globalEvents, [payload.rpName]: { ...current, filters: payload.filters } };
        },

        clearGlobalEvents(payload: { rpName: string }) {
            this.globalEvents = omit(this.globalEvents, payload.rpName);
            pendingReload.delete(payload.rpName);
        },

        async loadGlobalEvents(payload: { rpName: string, filters?: ActivityHistoryQuery }) {
            const { rpName, filters = {} } = payload;
            this.setFilters({ rpName, filters });
            if ((this.globalEvents[rpName] || createEmptyStatus()).loading) {
                pendingReload.add(rpName);
                return;
            }
            await this.fetchGlobalEvents(rpName);
        },

        // Reads the filters currently on the store rather than taking them as a parameter, so a
        // reload queued by loadGlobalEvents while this was in flight picks up whatever filters are
        // current by the time it actually runs.
        async fetchGlobalEvents(rpName: string) {
            const filters = (this.globalEvents[rpName] || createEmptyStatus()).filters;
            this.startLoading({ rpName, reset: true });
            try {
                const result = await appOf(this).gatewayClient.tprmUi()
                    .getGlobalActivityHistory(rpName, { skip: 0, limit: LIMIT, ...filters });
                this.finishLoading({
                    rpName,
                    records: result.records,
                    finished: result.records.length < LIMIT,
                    append: false,
                });
            } catch (error) {
                console.warn('load global events error', error);
                this.failLoading({ rpName });
            }
            if (pendingReload.delete(rpName)) {
                await this.fetchGlobalEvents(rpName);
            }
        },

        async loadNextGlobalEvents(payload: { rpName: string }) {
            const { rpName } = payload;
            const current = this.globalEvents[rpName] || createEmptyStatus();
            // A failed append leaves `finished` false, so whatever triggered this call (in
            // practice, InfiniteScroll re-firing on every re-render while the user sits at the
            // bottom of the list) is free to call again immediately. Gating on error as well as
            // loading/finished stops that request from being retried on its own; only an
            // explicit retry, which clears error first, is allowed through.
            if (current.loading || current.finished || current.error) {
                return;
            }
            this.startLoading({ rpName });
            try {
                const result = await appOf(this).gatewayClient.tprmUi()
                    .getGlobalActivityHistory(rpName, { skip: current.list.length, limit: LIMIT, ...current.filters });
                this.finishLoading({
                    rpName,
                    records: result.records,
                    finished: result.records.length < LIMIT,
                    append: true,
                });
            } catch (error) {
                console.warn('load next global events error', error);
                this.failLoading({ rpName });
            }
            // A filter applied while this page was loading is queued in pendingReload rather
            // than dropped. Draining it here as a full reload — instead of leaving it for the
            // next append — keeps a later page from being fetched with skip computed against
            // the pre-filter list length and appended onto those stale rows.
            if (pendingReload.delete(rpName)) {
                await this.fetchGlobalEvents(rpName);
            }
        },

        // The error guard in loadNextGlobalEvents means a plain call does nothing once a page
        // has failed. This clears the flag first so the explicit Retry action on the inline
        // error banner actually re-issues the request instead of being swallowed by that guard.
        async retryNextGlobalEvents(payload: { rpName: string }) {
            const { rpName } = payload;
            const current = this.globalEvents[rpName];
            if (current) {
                this.globalEvents = { ...this.globalEvents, [rpName]: { ...current, error: false } };
            }
            await this.loadNextGlobalEvents({ rpName });
        },
    },
});
